import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Video, Music, FileText, Link, X } from 'lucide-react';
import './scss/EmbedUrlModal.scss';

const TYPE_CONFIG = {
  video: {
    icon: Video,
    title: 'Embed video',
    placeholder: 'Paste YouTube, Vimeo, or video URL...',
    hint: 'Works with YouTube, Vimeo, and direct video links',
  },
  audio: {
    icon: Music,
    title: 'Embed audio',
    placeholder: 'Paste Spotify, SoundCloud, or audio URL...',
    hint: 'Works with Spotify, SoundCloud, and direct audio links',
  },
  file: {
    icon: FileText,
    title: 'Embed file',
    placeholder: 'Paste file or document URL...',
    hint: 'Paste a URL to embed external content',
  },
};

export default function EmbedUrlModal({ embedType, onSubmit, onClose }) {
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);
  const config = TYPE_CONFIG[embedType] || TYPE_CONFIG.file;
  const Icon = config.icon;

  useEffect(() => {
    // Focus the input on mount
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = useCallback((e) => {
    e?.preventDefault();
    const trimmed = url.trim();
    if (trimmed) {
      onSubmit(trimmed, embedType);
    }
  }, [url, embedType, onSubmit]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [onClose]);

  return createPortal(
    <div className="embed-url-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="embed-url-modal" onClick={(e) => e.stopPropagation()}>
        <div className="embed-modal-header">
          <div className="embed-modal-title">
            <Icon size={18} />
            <span>{config.title}</span>
          </div>
          <button className="embed-modal-close" onClick={onClose} type="button">
            <X size={16} />
          </button>
        </div>

        <form className="embed-modal-body" onSubmit={handleSubmit}>
          <div className="embed-input-wrapper">
            <Link size={16} className="embed-input-icon" />
            <input
              ref={inputRef}
              type="url"
              className="embed-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={config.placeholder}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <p className="embed-input-hint">{config.hint}</p>
          <div className="embed-modal-actions">
            <button type="button" className="embed-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="embed-btn-submit" disabled={!url.trim()}>
              Embed
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
